/* ============================================================
 * Naive UI 主题覆盖（xuntu theme overrides）
 *
 * 字段对齐：src/styles/tokens.css
 * 品牌色：--brand-blue / --brand-blue-dark / --brand-blue-light
 * 副品牌：--brand-green / --brand-green-dark
 * 文字：  --ink-900 / --ink-700 / --ink-500 / --ink-400
 * 边框：  --ink-200 / --ink-100
 * 半径：  --radius-lg / --radius-md
 * 字体：  --font-sans
 *
 * 用法：在 main.ts 用 `<NConfigProvider :theme-overrides="xuntuThemeOverrides">`
 * 包根，让所有 Naive UI 组件继承品牌色。
 * ============================================================ */

import type { GlobalThemeOverrides } from 'naive-ui'

/** Naive UI 通用变量覆盖（颜色 / 文字 / 边框 / 圆角 / 字体） */
const commonOverrides: GlobalThemeOverrides['common'] = {
  /* ============ Brand ============ */
  primaryColor: '#4a6cf7', // --brand-blue
  primaryColorHover: '#5a7cff',
  primaryColorPressed: '#3b5bdb', // --brand-blue-dark
  primaryColorSuppl: '#ebf0fe', // --brand-blue-light

  /* ============ Status ============ */
  successColor: '#00d4aa', // --brand-green
  successColorHover: '#1ce0bb',
  successColorPressed: '#00b894', // --brand-green-dark
  successColorSuppl: '#e0f7f0', // --brand-green-light
  warningColor: '#ffb300', // --status-warning
  errorColor: '#ff1744', // --status-danger
  errorColorHover: '#ff3a5e',
  errorColorPressed: '#e0113c',
  errorColorSuppl: '#ffebee', // --status-danger-light

  /* ============ Text ============ */
  textColor1: '#0a0e1a', // --ink-900
  textColor2: '#374151', // --ink-700
  textColor3: '#6b7a8f', // --ink-500
  textColorDisabled: '#94a3b8', // --ink-400
  placeholderColor: '#94a3b8', // --ink-400

  /* ============ Border ============ */
  borderColor: '#dce0e6', // --ink-200
  dividerColor: '#eef1f6', // --ink-100

  /* ============ Surface ============ */
  bodyColor: '#ffffff',
  cardColor: '#ffffff',
  modalColor: '#ffffff',
  popoverColor: '#ffffff',
  inputColor: '#ffffff',

  /* ============ Radius ============ */
  borderRadius: '12px', // --radius-lg（默认 input/button/card）
  borderRadiusSmall: '8px', // --radius-md（chip / tag）

  /* ============ Typography ============ */
  fontFamily:
    "'Noto Sans SC Variable', Inter, 'Source Han Sans SC', 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif",
  fontFamilyMono: "'JetBrains Mono', 'SF Mono', ui-monospace, monospace",

  /* ============ Heights（Input / Button） ============ */
  heightMedium: '44px', // 对齐现有 settings input 高度
  heightSmall: '36px',
  heightLarge: '52px',
}

const formOverrides: NonNullable<GlobalThemeOverrides['Form']> = {
  feedbackFontSizeMedium: '12px', // --text-xs
  feedbackFontSizeSmall: '11px',
  feedbackFontSizeLarge: '13px',
  labelFontSizeTopMedium: '13px', // --text-sm
  labelFontSizeTopSmall: '12px',
  labelFontSizeTopLarge: '14px',
  labelFontWeight: '600',
  labelPaddingVertical: '0 0 6px 0',
  feedbackPadding: '4px 0 0',
}

const inputOverrides: NonNullable<GlobalThemeOverrides['Input']> = {
  paddingMedium: '0 14px',
  paddingSmall: '0 12px',
  paddingLarge: '0 16px',
  borderRadius: '12px', // --radius-lg
  borderRadiusSmall: '8px',
  borderHover: '1px solid #5a7cff',
  borderFocus: '1px solid #4a6cf7',
  boxShadowFocus: '0 0 0 3px rgba(74, 108, 247, 0.15)',
}

// ⚠️ 当前项目使用自研 <AppButton>，**未使用** `<NButton>`。
// 以下 overrides 是预留配置，等 NButton 替换 AppButton 时直接生效。
// 修改前请先 grep 确认 `<NButton>` 是否已被引入，避免保留无用配置。
const buttonOverrides: NonNullable<GlobalThemeOverrides['Button']> = {
  paddingMedium: '0 22px', // 对齐 settings-btn 现有 padding
  paddingSmall: '0 16px',
  paddingLarge: '0 32px',
  borderRadiusMedium: '999px', // pill 形状（保留现有 AppButton 风格）
  borderRadiusSmall: '999px',
  fontWeight: '600',
  fontSizeMedium: '13px',
  fontSizeSmall: '12px',
  fontSizeLarge: '14px',
  heightMedium: '40px',
  heightSmall: '32px',
  heightLarge: '48px',
}

/**
 * NSelect 主题覆盖。
 *
 * 【关键】NSelect 不直接读 heightMedium —— naive-ui 内部用 <n-internal-selection>
 * 渲染 input 主体。NSelect 的真实高度 = InternalSelection.heightMedium + 自身 vertical padding。
 * 见 naive-ui GitHub Discussion #8096：Input / Select 都不读 NConfigProvider 的 size，
 * 必须通过 peers.InternalSelection 显式覆盖才能让 NSelect 与 NInput 等高。
 */
const selectOverrides: NonNullable<GlobalThemeOverrides['Select']> = {
  peers: {
    InternalSelection: {
      heightMedium: '44px', // 对齐 NInput（44px）
      heightSmall: '36px',
      heightLarge: '52px',
      borderRadius: '12px', // --radius-lg
      borderHover: '1px solid #5a7cff',
      borderFocus: '1px solid #4a6cf7',
      boxShadowFocus: '0 0 0 3px rgba(74, 108, 247, 0.15)',
      paddingSingle: '0 14px', // 单选时 input 横向内边距
      paddingMultiple: '3px 14px', // 多选时 input 横向内边距（保留 3px 给 tag）
    },
  },
}

export const xuntuThemeOverrides: GlobalThemeOverrides = {
  common: commonOverrides,
  Form: formOverrides,
  Input: inputOverrides,
  Select: selectOverrides,
  Button: buttonOverrides,
}
